import { useEffect, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { AlignLeft, Image, SquarePlay, ChevronDown } from 'lucide-react';
import { ProductPageSection } from '@/backend/models/productPageSection';
import { useProductPageStore } from '@/frontend/store/ProductPageStore';
import { resize } from '@/frontend/utils/parseReferences';
import { I18n } from '@/i18n/I18n';
import { useShallow } from 'zustand/react/shallow';
import { ProductPageContentType } from '@/backend/access/productPageContent/types';
import { ProductPageEditHeader } from './ProductPageEditHeader';
import { ProductPageContentEditor } from './ProductPageContentEditor';

interface ProductPageSectionEditorProps {
  section: ProductPageSection;
}

export function ProductPageSectionEditor({ section }: ProductPageSectionEditorProps) {
  const updateSection = useProductPageStore(state => state.updateSection);
  const deleteSection = useProductPageStore(state => state.deleteSection);
  const duplicateSection = useProductPageStore(state => state.duplicateSection);
  const reorderSections = useProductPageStore(state => state.orderSections);
  const sections = useProductPageStore(state => state.sections);
  const createContent = useProductPageStore(state => state.createContent);
  const contents = useProductPageStore(useShallow(state => state.contents.filter(c => c.productPageSectionId === section.id).sort((a, b) => a.position - b.position)));

  const [title, setTitle] = useState(section.title);
  const [description, setDescription] = useState(section.description);
  const descriptionId = `section-description-${section.id}`;

  useEffect(() => { resize(descriptionId); }, [descriptionId]);

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    resize(descriptionId);
  };

  const handleTitleBlur = () => {
    if (title !== section.title) {
      void updateSection({ ...section, title });
    }
  };

  const handleDescriptionBlur = () => {
    if (description !== section.description) {
      void updateSection({ ...section, description });
    }
  };

  const handleAddContent = (type: ProductPageContentType) => {
    void createContent({
      type,
      productPageSectionId: section.id,
      position: contents.length,
    });
  };

  return (
    <div className="light-card">
      <ProductPageEditHeader siblings={sections} item={section} reorder={reorderSections} duplicate={duplicateSection} deleteAction={deleteSection}>
        <span className="product-page-section-label">{I18n.get('Section')}</span>
      </ProductPageEditHeader>
      <div className="product-page-section-body">
        <div className="product-page-content-card">
          <Form.Group className="mb-2">
            <Form.Label className="form-label-neutral">{I18n.get('sectionTitle')}</Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleTitleBlur}
              className="form-control-neutral"
              aria-label={I18n.get('sectionTitle')}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="form-label-neutral">{I18n.get('sectionDescription')}</Form.Label>
            <Form.Control
              as="textarea"
              id={descriptionId}
              rows={1}
              value={description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              onBlur={handleDescriptionBlur}
              className="form-control-neutral"
              aria-label={I18n.get('sectionDescription')}
            />
          </Form.Group>
        </div>

        {contents.map((content) => (
          <ProductPageContentEditor key={content.id} content={content} />
        ))}
      </div>

      <div className="product-page-section-footer">
        <AddContentDropdown onAdd={handleAddContent} />
      </div>
    </div>
  );
}

function AddContentDropdown({ onAdd }: { onAdd: (type: ProductPageContentType) => void }) {
  return (
    <Dropdown drop='down'>
      <Dropdown.Toggle
        variant="primary"
        size="sm"
        className="fusa-menu-btn"
      >
        {I18n.get('add')}
        <ChevronDown size={14} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item
          onClick={() => onAdd(ProductPageContentType.TEXT)}
          className="d-flex align-items-center gap-2"
        >
          <AlignLeft size={16} />
          {I18n.get('text')}
        </Dropdown.Item>  
        <Dropdown.Item
          onClick={() => onAdd(ProductPageContentType.IMAGE)}
          className="d-flex align-items-center gap-2"
        >
          <Image size={16} />
          {I18n.get('image')}
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => onAdd(ProductPageContentType.VIDEO)}
          className="d-flex align-items-center gap-2"
        >
          <SquarePlay size={16} />
          {I18n.get('video')}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
